// src/components/ChatWindow.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { RoutesPaths } from "../../../Router/constant";
import { useNavigate } from "react-router";
import { getChatUserImage } from "../userList";
import ContentEditable from "react-contenteditable";
import { appUrl, backendUrl, capitalizeFirstLetter, PUSH_NOTIFICATION_MODULE, SUBSCRIPTION_MODULE_TYPE, SUPPORTED_PAYMENT_METHOD_NAME } from "../../../constants";
import TextMessageBox from "../TextMessageBox";
import Currency from "../../../components/currency";
import CurrencyFormat from "../../../components/currencyFormat/currencyFormat";
import { setFailedMessage } from "../../../actions/message";
import { uploadMultipleFileAction } from "../../../actions/files";
import {
  chatMediaAddAction,
  chatMediaPreviewerAction,
} from "../../../actions/chat";
import { getName } from "../messageDetail";
import EmojiPicker from "emoji-picker-react";
import {
  clearAdminAsUserAction,
  loginAdminAsUserAction,
} from "../../../actions/user";
import CreatePayment from "../CreatePayment";
import ViewProfileModal from "../../../components/ViewProfileModal";
import TimeFormat from "../../../components/timeFormat/timeFormat";
var holdCurrentDate = null;

export const downloadMediaOnFly = (item) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    const fileUrl = event.target.result;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = item.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  reader.readAsDataURL(item);
};

export const downloadMedia = (media) => {
  const url = backendUrl + media.filename;
  window.open(
    url,
    "_blank", // <- This is what makes it open in a new window.
  );
};

export default function ChatWindow({
  chat,
  messages,
  openProfile,
  highlightMessageId,
  currentUserId,
  onSend,
  showProfile,
  searchQuery,
}) {
  const [text, setText] = useState("");
  const [currentUserIdChange, setCurrentUserIdChange] = useState(null);
  const endRef = useRef();
  const [fileArray, setFileArray] = useState([]);
  const [base64Files, setBase64Files] = useState([]);
  const navigate = useNavigate();

  const { user, adminAsuser } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const textAreaRef = useRef(null);
  const sendMessageButton = useRef(null);
  const { chatMedia } = useSelector((state) => state.chat);
  const [emojiPopupToggle, setEmojiPopupToggle] = useState(false);
  const [mentionPopupToggle, setMentionPopupToggle] = useState(false);
  const [paymentLinkModal, setPaymentLinkModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const overlayRef = useRef();
  const emojiRef = useRef();
  const mentionRef = useRef();
  const mentionBtnRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (chat?._id !== currentUserIdChange) {
      setText("");
      setCurrentUserIdChange(currentUserId);
    }
  }, [currentUserId, chat]);

  // Scroll behavior: highlight takes priority; otherwise go to bottom
  useEffect(() => {
    if (highlightMessageId) {
      const el = document.getElementById(`message-${highlightMessageId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlighted");

        // Remove the class after 3 seconds
        setTimeout(() => {
          el.classList.remove("highlighted");
        }, 3000);
      }
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, highlightMessageId]);

  const send = async () => {
    const c = text.trim();
    if (c.length === 0 && fileArray.length === 0) {
      return;
    }

    const fileData = new FormData();
    for (const file of fileArray) {
      fileData.append("files", file);
    }
    if (fileArray.length > 0) {
      setFileArray([]);
      setBase64Files([]);
      const filesResp = await uploadMultipleFileAction(fileData);
      chatMediaAddAction({ channelId: currentUserId, media: filesResp });
      onSend(c, filesResp);
    } else {
      onSend(c);
    }
    setText("");
  };

  const getSeperatedDate = (messageDate) => {
    var showDate = false;
    var currentMessageDate = moment(messageDate).format("YYYYMMDD");
    showDate = holdCurrentDate !== currentMessageDate ? true : false;
    holdCurrentDate = currentMessageDate;
    if (showDate) {
      return (
        <div className="seperate-line">
          <span>
            {moment(messageDate).format("ddd, MMM D")}{" "}
            {moment(messageDate).format("Y") !== moment().format("Y")
              ? "," + moment(messageDate).format("Y")
              : ""}
          </span>
        </div>
      );
    } else {
      return null;
    }
  };

  const addToDiv = (data, value) => {
    const chatBox = textAreaRef.current;
    chatBox.focus();
    chatBox.innerHTML.split("@").join("");
    pasteHtmlAtCaret(
      value
        ? ` ${data.emoji} `
        : `<span class="tagName" contentEditable="false">${data?.first_name + " " + data?.last_name || data?.email}</span> `,
    );
    overlayRef.current.click();
  };

  const pasteHtmlAtCaret = (html) => {
    var sel, range;
    if (window.getSelection) {
      // IE9 and non-IE
      sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        range = sel.getRangeAt(0);
        range.deleteContents();

        // Range.createContextualFragment() would be useful here but is
        // non-standard and not supported in all browsers (IE9, for one)
        var el = document.createElement("div");
        el.innerHTML = html;
        var frag = document.createDocumentFragment(),
          node,
          lastNode;
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node);
        }
        range.insertNode(frag);

        // Preserve the selection
        if (lastNode) {
          range = range.cloneRange();
          range.setStartAfter(lastNode);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
    } else if (document.selection && document.selection.type != "Control") {
      // IE < 9
      document.selection.createRange().pasteHTML(html);
    }

    setText(textAreaRef.current.innerHTML);
  };

  const handleClickOutside = (event) => {
    if (emojiRef.current && !emojiRef.current.contains(event.target)) {
      setEmojiPopupToggle(false);
    }
    if (mentionRef.current && !mentionRef.current.contains(event.target)) {
      setMentionPopupToggle(false);
    }
  };

  useEffect(() => {
    // Add event listener for clicks
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (adminAsuser) {
      window.open(adminAsuser, "_blank");
      dispatch(clearAdminAsUserAction());
    }
  }, [dispatch, adminAsuser]);

  const handleSpanClick = (event) => {
    if (event.target.classList.contains("messageExternalLinkCompanyChange")) {
      const companyId = event.target.getAttribute("companyid");
      dispatch(loginAdminAsUserAction({ company: companyId, user: null }));
    }
  };

  const dateFormatShow = (dateFilter) => {
    var date = new Date(dateFilter).toLocaleTimeString([], {
      hourCycle: "h12",
      hour: "2-digit",
      minute: "2-digit",
    });
    return date;
  };

  const handleKeyPress = (event) => {
    if (
      event.charCode === 13 &&
      !event.ctrlKey &&
      !event.shiftKey &&
      event.key === "Enter"
    ) {
      event.preventDefault(); // Prevent adding a newline
      // onMessageSend()
      sendMessageButton.current.click();
    }
  };

  const handleViewProfile = (profile) => {
    setProfileData(profile);
    setOpen(true);
  };

  const getDisplayName = (item) => {
    if (item?.name) return item.name;
    const fn = item?.first_name || "";
    const ln = item?.last_name || "";
    const full = `${fn}${fn && ln ? " " : ""}${ln}`.trim();
    return full || "-";
  };

  const getInitialLetter = (item) => {
    const n = item?.name || item?.first_name || "";
    return n ? String(n).substring(0, 1) : "-";
  };

  const gotoPaymentStatus = (data) => {
    window.open(`${appUrl}ps/${data?.data?.plan?.slug}`, "_blank")
  }

  const printMessage = (message) => {
    const isReceiver = user.id !== message?.sender?.id;
    const isSender = user.id === message?.sender?.id;
    const senderName =
      message?.sender?.first_name +
      (message?.sender?.last_name ? " " + message?.sender?.last_name : "") ||
      "Someone";
    return (
      <>
        {message?.module === PUSH_NOTIFICATION_MODULE.FILE ? (
          <>
            {message.content && (
              <TextMessageBox
                searchMessageId={highlightMessageId}
                message={message}
                searchQuery={searchQuery}
              />
            )}
            <div className="message-box-hold-outer">
              {message.data.map((item, index) => (
                <div className="message-box-hold" key={index}>
                  <span
                    key={index}
                    className="addMedia"
                    onClick={() =>
                      item?.mimetype.startsWith("image/")
                        ? setOpenImageViewer(item)
                        : downloadMedia(item)
                    }
                  >
                    {item?.mimetype.startsWith("image/") ? (
                      <span
                        className="imagePreviewChat"
                        style={{
                          backgroundImage: `url(${backendUrl + item.filename})`,
                        }}
                      ></span>
                    ) : (
                      <>
                        <span className="addMediaPadding">
                          <img
                            className="imageView"
                            src="assets/img/imageView.svg"
                            alt=""
                          />
                          &nbsp;
                          <span className="fileName">{item.originalName}</span>
                        </span>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : message?.module === PUSH_NOTIFICATION_MODULE.ESIGN ? (
          <div className="e-sign-pdf-message">
            <div className="e-sign-pdf-img-hold">
              <div className="e-sign-pdf-img"></div>
            </div>
            <div className="e-sign-pdf-name">
              {message?.data?.type === 1 ? (
                <h6>
                  {message?.data?.documentName}.pdf has been initiated by{" "}
                  {message?.data?.senderFullName} to be e-signed
                </h6>
              ) : message?.data?.type === 3 ? (
                <h6>Everyone has signed {message?.data?.documentName}.pdf</h6>
              ) : (
                <h6>
                  {message?.data?.signerFullName} has received{" "}
                  {message?.data?.documentName}.pdf to be e-signed.
                </h6>
              )}
              <div className="titleDesc showLess">
                <h3>{message?.data?.title}</h3>
                <p>{message?.data?.message}</p>
              </div>
              <div className="e-sign-pdf-button">
                <div className="e-sign-pdf-button-with-size">
                  {/* <p className="e-sign-pdf-size">$100</p> */}
                  {typeof message?.data?.signers === "object" &&
                    message?.data?.signers.length > 0 && (
                      <div className="e-sign-pdf-signer-section">
                        {message?.data?.signers.map((fruit, index) => (
                          <p key={index} className="e-sign-pdf-signer">
                            <strong>Signer {index + 1}:</strong>&nbsp;
                            {fruit.first_name + " " + fruit.last_name}
                            {message?.data?.type === 1 && `, ${fruit.email}`}
                          </p>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        ) : message?.module === PUSH_NOTIFICATION_MODULE.PAYMENTLINK ? (
          <div className="payment-link-message">
            <div className="payment-link-hold">
              <div className="cursorPointer payment-link-img"></div>
            </div>
            <div>
              <h6>Quotation sent</h6>
              {message?.data && (
                <>
                  <p className="message">
                    Purpose: <strong>{message?.data?.name}</strong>
                  </p>
                  <p className="message mb-2">
                    Amount:{" "}
                    <strong>
                      <Currency currency={message?.data?.currency?.value} />{" "}
                      <CurrencyFormat
                        currency={message?.data?.currency?.value}
                        value={parseFloat(message?.data?.price)}
                        decimal={2}
                      />
                    </strong>
                  </p>
                </>
              )}
            </div>
          </div>
        ) : message?.module === PUSH_NOTIFICATION_MODULE.NAME_EMAIL ? (
          <div className="payment-link-message">
            <div>
              <p className="message">
                Name: {message?.data?.first_name} {message?.data?.last_name}
              </p>
              <p className="message">Email: {message?.data?.email}</p>
            </div>
          </div>
        ) : message?.module === PUSH_NOTIFICATION_MODULE.BOB_PROFIILE ? (
          <div className="profileMessageWrap">
            <h6 className="profileMessageHeader">
              {isSender
                ? "You shared a profile suggestion"
                : `${senderName} shared a profile suggestion with you`}
            </h6>

            <div
              className="profileCardChat"
              onClick={() => handleViewProfile(message.data)}
            >
              <div className="profileCardLeft">
                {message?.data?.imageUrl && !imageError?.[message?.data?.id] ? (
                  <img
                    src={message?.data?.imageUrl}
                    className="profileAvatar"
                    onError={() =>
                      setImageError((prev) => ({
                        ...prev,
                        [message?.data?.id]: true,
                      }))
                    }
                  />
                ) : (
                  <div className="profileAvatarFallback">
                    {getInitialLetter(message?.data)}
                  </div>
                )}
              </div>

              {/* RIGHT */}

              <div className="profileCardRight">
                {/* NAME ROW */}

                <div className="profileNameRow">
                  <span className="profileName">
                    {getDisplayName(message?.data)}
                  </span>

                  {message?.data?.verified && (
                    <span className="verifiedBadge">✓</span>
                  )}
                </div>
                <div className="profileMetaRow">
                  {message?.data?.location && (
                    <span>📍 {message.data.location}</span>
                  )}
                  {message?.data?.headline && (
                    <span>• {message.data.headline}</span>
                  )}
                  {message?.data?.industry && (
                    <span>• {message.data.industry}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) :
          message?.module === PUSH_NOTIFICATION_MODULE.PAYMENTLINKPAID ? (
            <div
              className="payment-receipt-card cursorPointer"
              onClick={() => gotoPaymentStatus(message)}
            >
              <h6 className={`receipt-title ${!isReceiver ? "adminText" : ""}`}>
                {isReceiver
                  ? message?.sender?.first_name
                    ? `${message?.sender?.first_name} ${message?.sender?.last_name}`
                    : "Admin has sent you payment receipt"
                  : "Payment receipt sent"}
              </h6>

              {message?.data && (
                <>
                  <p className={`receipt-row ${!isReceiver ? "adminText" : ""}`}>
                    Amount:{" "}
                    <strong>
                      <Currency currency={message?.data?.payment?.currency || ""} />
                      <CurrencyFormat
                        currency={message?.data?.payment?.currency}
                        value={parseFloat(
                          message?.data?.payment?.amount / 100
                        )}
                        decimal={2}
                      />
                    </strong>
                  </p>

                  <p className={`receipt-row ${!isReceiver ? "adminText" : ""}`}>
                    Status:{" "}
                    <strong>
                      {capitalizeFirstLetter(
                        message?.data?.payment?.payment_status
                      )}
                    </strong>
                  </p>

                  <p className={`receipt-row ${!isReceiver ? "adminText" : ""}`}>
                    Transaction ID:{" "}
                    <strong>{message?.data?.payment?.paymentId}</strong>
                  </p>

                  {message?.data?.plan?.planType?.value ===
                    SUBSCRIPTION_MODULE_TYPE.CUSTOM && (
                      <p className={`receipt-row ${!isReceiver ? "adminText" : ""}`}>
                        Quotation ID:{" "}
                        <strong>
                          Quotation {message?.data?.plan?.quotationId}
                        </strong>
                      </p>
                    )}

                  <p className={`receipt-row ${!isReceiver ? "adminText" : ""}`}>
                    Payment Time:{" "}
                    <strong>
                      <TimeFormat
                        time={message?.data?.payment?.createdAt}
                      />
                    </strong>
                  </p>

                  <p className={`receipt-row ${!isReceiver ? "adminText" : ""}`}>
                    Payment Method:{" "}
                    <strong>
                      {
                        SUPPORTED_PAYMENT_METHOD_NAME[
                        message?.data?.payment
                          ?.paymentMethodSelection
                        ]
                      }
                    </strong>
                  </p>
                </>
              )}
            </div>
          ) : (
            <TextMessageBox
              searchMessageId={highlightMessageId}
              message={message}
              searchQuery={searchQuery}
            />
          )}
      </>
    );
  };

  const uploadFiles = (event) => {
    const maxfileUploadSize = 1024 * 1024 * 20;
    const files = Array.from(event.target.files);

    const readers = [];
    const filesUpload = [];

    for (const file of files) {
      if (file.size > maxfileUploadSize) {
        event.target.value = null;
        dispatch(setFailedMessage("Maximum file upload size is 20mb."));
        return;
      }

      // Push the file to be uploaded to backend
      filesUpload.push(file);

      // Create a promise for reading file as base64
      if (file.type.startsWith("image/")) {
        readers.push(
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }),
        );
      } else {
        readers.push(
          new Promise((resolve, reject) => {
            resolve(null);
          }),
        );
      }
    }

    // Update state with uploaded files
    setFileArray((oldArray) => [...oldArray, ...filesUpload]);

    // Read all files asynchronously and update base64Files state
    Promise.all(readers).then((results) => {
      setBase64Files((oldBase64Array) => [...oldBase64Array, ...results]);
    });

    event.target.value = null; // Reset input field
  };

  const removeMedia = (index) => {
    fileArray.splice(index, 1);
    base64Files.splice(index, 1);
    setFileArray([...fileArray]);
    setBase64Files([...base64Files]);
  };

  const setOpenImageViewer = (item) => {
    const indexValueChatMedia = chatMedia.findIndex(
      (x) => x.filename === item.filename,
    );
    openImageViewer(indexValueChatMedia);
  };

  const openImageViewer = useCallback((index) => {
    dispatch(chatMediaPreviewerAction(index, true));
  }, []);

  function handlePaste(e) {
    e.preventDefault();

    // Get plain text from clipboard
    const text = e.clipboardData.getData("text/plain");

    // Insert plain text at cursor
    document.execCommand("insertText", false, text?.trimRight());
  }

  if (!chat) {
    return (
      <div style={{ padding: 20, fontStyle: "italic" }}>Loading chat…</div>
    );
  }

  return (
    <>
      <div className="messageHeader">
        <div className="userName">
          <div>
            <h4>
              <span>{getName(chat, user)}</span>
            </h4>
            <p>
              {moment().format("h:mm") + " GMT" + moment().format("Z")}&nbsp;
            </p>
          </div>
        </div>
        <div className="openDashboard">
          <div className="arrowIcon" onClick={openProfile}>
            <img
              className={showProfile ? "showProfileSec" : ""}
              src="assets/img/openDashboard.svg"
              alt=""
            />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        id="scrollable"
        className={
          fileArray.length > 0
            ? "addedMedia messageHolder customScroll"
            : "messageHolder customScroll"
        }
        style={{
          display: "flex",
          flexDirection: "column-reverse",
          width: "100%",
        }}
      >
        <div className="messageList">
          {messages?.map((msg, index) => (
            <div key={index}>
              <div>
                {/* {getSeperatedDate(msg.dateCreated)} */}
                {msg?.system ? (
                  <div className="joinedLeaveChat">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: msg?.content,
                      }}
                    ></span>
                  </div>
                ) : (
                  <div
                    id={"message-" + msg?._id}
                    className={
                      user?.id === msg?.sender?.id
                        ? "messageListOuter ownProperty"
                        : "messageListOuter"
                    }
                  >
                    <div className="messageListInner" onClick={handleSpanClick}>
                      <>
                        {user?.id !== msg?.sender?.id && (
                          <div
                            style={{
                              backgroundImage: `url(${getChatUserImage(
                                chat?.participants,
                                user,
                                msg?.sender,
                              )})`,
                            }}
                            className="profile_photo"
                          ></div>
                        )}
                      </>

                      <div className="messagebyUser">
                        <div className="userName">
                          <div className="userMessage">
                            {msg.sender ? (
                              <>
                                {user?.id !== msg.sender?.id && (
                                  <h4>
                                    <span>
                                      {msg.sender?.first_name +
                                        " " +
                                        msg.sender?.last_name}
                                    </span>
                                    &nbsp;
                                  </h4>
                                )}
                              </>
                            ) : (
                              <h4>
                                <span>kimbocorp assistant</span>
                                &nbsp;
                              </h4>
                            )}
                            <div className="messagingService">
                              {printMessage(msg)}
                              {/* <span
                                dangerouslySetInnerHTML={{
                                  __html: msg.content,
                                }}
                              ></span> */}
                            </div>
                            {user.id === msg.sender?.id && (
                              <span className="currentMessageTime">
                                {dateFormatShow(msg.createdAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </div>
      <div className="row message-input">
        <div className="col-lg-12">
          {/* {typingData.length > 0 && <div className="typing">{typingInfo}</div>} */}
          <div className="inputHolder">
            {fileArray.length > 0 && (
              <div className="mediaInner">
                {fileArray.map((item, index) => (
                  <span key={index} className="removeImageHolder">
                    <img
                      src="assets/img/removeMedia.svg"
                      alt=""
                      className="removeMedia"
                      onClick={() => removeMedia(index)}
                    />

                    <span key={index}>
                      <span
                        className="addMedia"
                        onClick={() => downloadMediaOnFly(item)}
                      >
                        {item?.type.startsWith("image/") ? (
                          <span
                            className="imagePreviewChat"
                            style={{
                              backgroundImage: `url(${base64Files[index]})`,
                            }}
                          ></span>
                        ) : (
                          <>
                            <span className="addMediaPadding">
                              <img
                                className="imageView"
                                src="assets/img/imageView.svg"
                                alt=""
                              />
                              &nbsp;
                              <span className="fileName">{item.name}</span>
                            </span>
                          </>
                        )}
                      </span>
                    </span>
                  </span>
                ))}
              </div>
            )}
            <div
              className={
                fileArray.length > 0
                  ? "addedMedia userListHolder"
                  : "userListHolder"
              }
            >
              <ContentEditable
                id="text"
                innerRef={textAreaRef}
                html={text}
                onPaste={handlePaste}
                onChange={(e) => setText(textAreaRef.current.innerText)}
                disabled={false} // use true to disable edition
                // onChange={(event) => setValue(event.target.value)} // handle innerHTML change
                // onKeyUp={handleKeyUpTrigger}
                // onBlur={keepFocus}
                // onChange={handleMessageKeyDownEventLocal}
                onKeyPress={handleKeyPress}
                className="textarea customScroll"
                tagName="div"
                placeholder="Write your message here..."
              />
            </div>
          </div>
        </div>

        <div className="customButtonHolder">
          <div className="col-lg-4 customButtonsEditor">
            <div className="col-lg-12">
              <div className="styleMessage">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    document.execCommand("bold", false, null);
                  }}
                  className="editText"
                >
                  <img src="assets/img/boldMessage.svg" alt="" />
                </button>
              </div>
            </div>
          </div>
          <div className="col-lg-4 customButtons">
            <div className="col-lg-12" ref={overlayRef}>
              <div
                ref={mentionRef}
                id="popover-basic"
                className={`emoji-popover webkitScroll ${!mentionPopupToggle ? "collapse" : "expand"
                  }`}
              >
                <div className="">
                  <div
                    className={
                      fileArray.length > 0
                        ? "addedMedia mentionHolder customScroll"
                        : "mentionHolder customScroll"
                    }
                  >
                    <div className="mentionInner">
                      <ul>
                        {chat?.participants.map(
                          (item, index) =>
                            user.email !== item.email && (
                              <li
                                key={index}
                                onClick={() => addToDiv(item, false)}
                              >
                                <div
                                  className="image"
                                  style={{
                                    backgroundImage: `url(${getChatUserImage(
                                      chat?.participants,
                                      user,
                                      item.email,
                                    )})`,
                                  }}
                                ></div>
                                <p>
                                  {item?.first_name + " " + item?.last_name ||
                                    item?.email}
                                </p>
                              </li>
                            ),
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => setPaymentLinkModal(true)}>
                <img src="assets/img/paymentLink.svg" alt="" />
              </button>
              <button
                ref={mentionBtnRef}
                onClick={() => setMentionPopupToggle(!mentionPopupToggle)}
              >
                <img src="assets/img/mentionMessage.svg" alt="" />
              </button>

              <div
                ref={emojiRef}
                id="popover-basic"
                className={`emoji-popover webkitScroll ${!emojiPopupToggle ? "collapse" : "expand"
                  }`}
                position="right"
              >
                <div className="">
                  <EmojiPicker onEmojiClick={(data) => addToDiv(data, true)} />
                </div>
              </div>
              <button onClick={() => setEmojiPopupToggle(!emojiPopupToggle)}>
                <img
                  src="assets/img/emojiIcon.svg"
                  className="emojiIcon"
                  alt=""
                />
              </button>
              <label>
                <input type="file" hidden onChange={uploadFiles} multiple />
                <img src="assets/img/mediaShare.svg" alt="" />
              </label>
              <button type="submit" ref={sendMessageButton} onClick={send}>
                <img src="assets/img/sendBtn.svg" alt="" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <CreatePayment
        show={paymentLinkModal}
        onClose={() => setPaymentLinkModal(false)}
        conversation={chat}
      />

      <ViewProfileModal
        show={open}
        onClose={() => setOpen(false)}
        profile={profileData}
      />
    </>
  );
}
